import React, {useEffect, useState} from 'react';
import {Dialog, DialogTitle, DialogContent, TextField, Button, Box, CircularProgress, IconButton} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import instance from '../../../instance';
import CallNotification from '../../../context/NotificationContext';

interface ResetPasswordPopupProps {
    open: boolean;
    userId: number | null;
    serviceCenterId: number | null;
    onClose: () => void;
    onSuccess?: () => void;
}

const ResetMerchantPasswordPopup = ({open, userId, serviceCenterId, onClose, onSuccess}: ResetPasswordPopupProps) => {
    const {handleErrorNotification} = CallNotification();

    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState<{password?: string; confirm?: string}>({});

    useEffect(() => {
        if (!open) {
            setPassword('');
            setConfirmPassword('');
            setShowPassword(false);
            setShowConfirmPassword(false);
            setLoading(false);
            setErrors({});
        }
    }, [open]);

    const validate = () => {
        const err: typeof errors = {};
        if (!password) {
            err.password = 'Пароль обязателен';
        } else if (password.length < 7) {
            err.password = 'Мин. 7 символов';
        } else if (!/[a-zA-Z]/.test(password) || !/\d/.test(password) || !/[!-~]/.test(password)) {
            err.password = 'Латинские буквы, цифры и спец. символы';
        }

        if (!confirmPassword) {
            err.confirm = 'Подтвердите пароль';
        } else if (password !== confirmPassword) {
            err.confirm = 'Пароли должны совпадать';
        }

        setErrors(err);
        return Object.keys(err).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) return;

        if (!userId) {
            setErrors({confirm: 'Не указан пользователь для сброса пароля'});
            return;
        }

        setLoading(true);
        try {
            const response = await instance.post('/LK', {
                method: 'ResetPassword',
                params: {
                    user_id: userId,
                    service_center_id: serviceCenterId,
                    password: password,
                },
            });

            handleErrorNotification(response, true);

            if (response.data?.error === 0) {
                if (onSuccess) onSuccess();
                onClose();
            } else {
                if (response.data?.error_description && String(response.data.error_description).length > 0) {
                    setErrors(prev => ({...prev, confirm: String(response.data.error_description)}));
                } else {
                    setErrors(prev => ({...prev, confirm: 'Ошибка.'}));
                }
            }
        } catch (err) {
            setErrors(prev => ({...prev, confirm: 'Ошибка при обращении к серверу'}));
            console.error('ResetPassword error', err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
            <div style={{position: 'relative', backgroundColor: '#f5f7ff'}}>
                <DialogTitle sx={{pr: 5}}>Сброс пароля мерчанта</DialogTitle>
                <IconButton onClick={onClose} style={{position: 'absolute', right: 8, top: 8}}>
                    <CloseIcon />
                </IconButton>
            </div>

            <DialogContent>
                {loading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" pt={2} pb={4}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Box display="flex" flexDirection="column" gap={2} pb={1}>
                        <TextField
                            label="Новый пароль"
                            type={showPassword ? 'text' : 'password'}
                            value={password}
                            onChange={e => setPassword(e.target.value)}
                            error={!!errors.password}
                            helperText={errors.password}
                            fullWidth
                            autoComplete="new-password"
                            InputProps={{
                                endAdornment: (
                                    <IconButton aria-label="toggle password visibility" onClick={() => setShowPassword(s => !s)} edge="end">
                                        {showPassword ? <Visibility /> : <VisibilityOff />}
                                    </IconButton>
                                ),
                            }}
                        />

                        <TextField
                            label="Новый пароль еще раз"
                            type={showConfirmPassword ? 'text' : 'password'}
                            value={confirmPassword}
                            onChange={e => setConfirmPassword(e.target.value)}
                            error={!!errors.confirm}
                            helperText={errors.confirm}
                            fullWidth
                            autoComplete="new-password"
                            InputProps={{
                                endAdornment: (
                                    <IconButton
                                        aria-label="toggle confirm password visibility"
                                        onClick={() => setShowConfirmPassword(s => !s)}
                                        edge="end"
                                    >
                                        {showConfirmPassword ? <Visibility /> : <VisibilityOff />}
                                    </IconButton>
                                ),
                            }}
                        />

                        <Box sx={{display: 'flex', gap: 1, justifyContent: 'flex-start', mt: 1}}>
                            <Button variant="contained" onClick={() => void handleSubmit()} disabled={loading}>
                                Сохранить
                            </Button>
                            <Button variant="text" onClick={onClose} disabled={loading}>
                                Отмена
                            </Button>
                        </Box>
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default ResetMerchantPasswordPopup;
